import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CreditCard, Coins } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KIND_LABELS = { bullion: "Coin Sales", membership: "Memberships", pass: "Session Passes", league_entry: "League Entries", tournament_entry: "Tournament Entries", session_booking: "Session Bookings", split_pay: "Split Payments", store: "Store" };

export default function PaymentsPanel() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/payments-summary").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  if (!data) return null;
  const kinds = Object.entries(data.card.by_kind || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="bg-pb-surface border border-pb-border p-6 mb-8" data-testid="payments-panel">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="w-4 h-4 text-pb-red" />
        <span className="font-display font-bold text-lg uppercase">Payments — Card vs Coin</span>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Live Stripe card revenue next to BULLION coin spend across the whole ecosystem.</p>
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="border border-pb-red/40 bg-pb-red/5 p-5" data-testid="payments-card-box">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-pb-red" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Card · Stripe Live</span>
          </div>
          <div className="font-display font-black text-4xl text-pb-red" data-testid="payments-card-total">{usd(data.card.total)}</div>
          <div className="text-xs text-zinc-500 mt-1">{data.card.count} payments all-time · <span className="text-zinc-300">{usd(data.card.month)} this month</span></div>
          {kinds.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {kinds.map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs">
                  <span className="text-zinc-400">{KIND_LABELS[k] || k}</span>
                  <span className="font-mono text-zinc-300">{usd(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-amber-500/40 bg-amber-500/5 p-5" data-testid="payments-coin-box">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Bullion Coin</span>
          </div>
          <div className="font-display font-black text-4xl text-amber-400" data-testid="payments-coin-total">
            {data.coin.coins_total.toLocaleString()}<span className="text-sm text-zinc-500 ml-1">BLC</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">≈ {usd(data.coin.usd_equiv)} at current rate · {data.coin.count} coin payments</div>
          <div className="mt-4 space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-zinc-400">Spent this month</span>
              <span className="font-mono text-zinc-300">{data.coin.coins_month.toLocaleString()} BLC (≈ {usd(data.coin.usd_equiv_month)})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Coin sales — cash collected</span>
              <span className="font-mono text-pb-red">{usd(data.coin.sales_cash_collected)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
